import * as React from "react";
import { Popup, useMap } from "react-leaflet";
import MarkerClusterContainer from "./MarkerClusterContainer";
import CartPopUp from "./cartPopUp";
import useAuth from "../../hooks/Auth/useAuth";

export function MapRef({
  width,
  height,
  OnlieMarkers,
  OfflineMarkes,
  cartToShowPath,
  setCartToShowPath,
  cartsPaths,
  setOnHoverCart,
  onHoverCart,
  defaultPoint,
  selectedCart,
  setSelectedCart,
  staticPopup,
  setStaticPopup,
  isPhoneSize,
  onStartReverse,
}) {
  const mapRef = useMap();
  const [onlineMarkers, setOnlineMarkers] = React.useState(OnlieMarkers);
  const [offlineMarkers, setOfflineMarkers] = React.useState(OfflineMarkes);
  const [lockedPopupKey, setLockedPopupKey] = React.useState(null);
  const { isPhoneSize: ctxIsPhoneSize } = useAuth();
  const phone = typeof isPhoneSize === 'boolean' ? isPhoneSize : ctxIsPhoneSize;

  React.useEffect(() => {
    if (onHoverCart == null ) {
      setOnlineMarkers(OnlieMarkers);
      setOfflineMarkers(OfflineMarkes);
    }
  }, [OnlieMarkers, OfflineMarkes]);

  // If switching to phone-size, close the Leaflet popup (mobile uses a different UI)
  React.useEffect(() => {
    if (phone && staticPopup?.position) {
      setStaticPopup({ position: null, cartInfo: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const closePopup = React.useCallback(() => {
    setStaticPopup({ position: null, cartInfo: null });
  }, [setStaticPopup]);

  // Auto-close the popup if the cart is no longer in the online markers list (disconnect case)
  React.useEffect(() => {
    if (!staticPopup?.cartInfo?.cart_number) return;
    const cartNumber = staticPopup.cartInfo.cart_number;
    const stillOnline = Array.isArray(OnlieMarkers)
      ? OnlieMarkers.some((c) => Number(c?.cart_number) === Number(cartNumber))
      : false;
    if (!stillOnline) {
      closePopup();
    }
  }, [OnlieMarkers, staticPopup?.cartInfo?.cart_number, closePopup]);

  // Simple: direct popup tracking without extra state/animation.
  // Best practice: mutate Leaflet layer rather than bouncing through React state each tick.
  const popupRef = React.useRef(null);
  React.useEffect(() => {
    if (phone) return; // desktop only
    if (!popupRef.current) return;
    if (!staticPopup?.cartInfo) return;
    const cartNumber = staticPopup.cartInfo.cart_number;
    const latest = Array.isArray(OnlieMarkers) ? OnlieMarkers.find(c => c.cart_number === cartNumber) : null;
    if (!latest || !Array.isArray(latest.cart_location)) return;
    const [lat, lng] = latest.cart_location;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    const popup = popupRef.current; // Leaflet Popup instance
    const current = popup.getLatLng();
    if (!current || Math.abs(current.lat - lat) > 1e-8 || Math.abs(current.lng - lng) > 1e-8) {
      popup.setLatLng([lat, lng]);
    }
  }, [OnlieMarkers, staticPopup?.cartInfo, phone]);

  // Derive the freshest cartInfo for the popup to avoid stale details (like mission updates)
  const latestCartInfo = React.useMemo(() => {
    if (!staticPopup?.cartInfo?.cart_number) return null;
    const cartNumber = staticPopup.cartInfo.cart_number;
    const latest = Array.isArray(OnlieMarkers)
      ? OnlieMarkers.find((c) => c.cart_number === cartNumber)
      : null;
    return latest || null;
  }, [OnlieMarkers, staticPopup?.cartInfo]);

  const computedPopupKey = React.useMemo(() => {
    const c = latestCartInfo;
    const cartNum = c?.cart_number;
    const missionFromPaths = cartsPaths?.[cartNum]?.mission;
    const keyMissionId = missionFromPaths?.missionId || c?.mission_id || '';
    const keyMissionType = missionFromPaths?.missionType || missionFromPaths?.type || c?.mission_type || '';
    const missionKey = `${keyMissionId}|${keyMissionType}`;
    return `popup-${cartNum ?? 'na'}-${missionKey}`;
  }, [latestCartInfo, cartsPaths]);
  const popupKey = lockedPopupKey ?? computedPopupKey;

  const lockPopupKey = React.useCallback(() => {
    setLockedPopupKey((prev) => (prev ?? computedPopupKey));
  }, [computedPopupKey]);

  const releasePopupKey = React.useCallback(() => {
    setLockedPopupKey(null);
  }, []);

  React.useEffect(() => {
    if (!staticPopup?.position) {
      setLockedPopupKey(null);
    }
  }, [staticPopup?.position]);

  return (
    <>
      <MarkerClusterContainer
        cartToShowPath={cartToShowPath}
        mapRef={mapRef}
        width={width}
        height={height}
        OnlieMarkers={onlineMarkers}
        OfflineMarkers={offlineMarkers}
        setCartToShowPath={setCartToShowPath}
        cartsPaths={cartsPaths}
        setOnHoverCart={setOnHoverCart}
        onHoverCart={onHoverCart}
        defaultPoint={defaultPoint}
        selectedCart={selectedCart}
        setSelectedCart={setSelectedCart}
        setStaticPopup={setStaticPopup}
      />

      {/* Desktop / tablet leaflet popup */}
      {!phone && staticPopup?.position && latestCartInfo && (
        <Popup
          key={popupKey}
          ref={popupRef}
          position={staticPopup.position}
          autoClose={false}
          closeOnClick={false}
          offset={staticPopup.popupOffset || [0, -50]}
          eventHandlers={{ remove: closePopup }}
        >
          <CartPopUp
            cartInfo={latestCartInfo}
            offline={!latestCartInfo.online}
            cartToShowPath={cartToShowPath}
            cartsPaths={cartsPaths}
            addCartTOHighPatsh={setCartToShowPath}
            selectedCart={selectedCart}
            setSelectedCart={setSelectedCart}
            setStaticPopup={setStaticPopup}
            onLockPopupKey={lockPopupKey}
            onReleasePopupKey={releasePopupKey}
            onStartReverse={onStartReverse}
          />
        </Popup>
      )}
    </>
  );
}
